import translate from '@translate';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TabType } from 'src/components/Tabs';
import { useCheckPermission } from 'src/utils/checkPermission';

const WEBHOOK_SETTINGS_SEGMENTS = ['webhooks', 'webhook_records', 'webhook_history'];

const getSettingsSegment = (pathname: string): string => pathname.split('/')[2] ?? '';

const isWebhookSettingsSegment = (segment: string): boolean => WEBHOOK_SETTINGS_SEGMENTS.includes(segment);

export const getSettingsTopBlockParams = (): {
    tabs: TabType[];
    selected: string;
    handleChange: (newUrl: string) => void;
} => {
    const navigate = useNavigate();
    const location = useLocation();

    const handleChange = useCallback((newUrl: string) => {
        navigate('/settings/' + newUrl);
    }, [navigate]);

    const settingsSegment = getSettingsSegment(location.pathname);
    const selected = isWebhookSettingsSegment(settingsSegment) ? 'webhooks' : settingsSegment;

    const skladonMappingsRoles = useCheckPermission('SKLADON_MAPPING.LIST');
    const widgetPermissions = useCheckPermission('SETTINGS.WIDGET.LIST');
    const providerCompaniesProviders = useCheckPermission('PROVIDER_COMPANIES.LIST');
    const providerTransferCompaniesProviders = useCheckPermission('PROVIDER_TRANSFER_COMPANY.LIST');
    const webhookPermission = useCheckPermission('WEBHOOK.LIST');
    const tabs = useMemo(
        () => [
            {
                value: 'provider_companies',
                label: translate('settings.providerCompanies'),
                hidden: !providerCompaniesProviders,
            },
            {
                value: 'provider_transfer_companies',
                label: translate('settings.providerTransferCompanies'),
                hidden: !providerTransferCompaniesProviders,
            },
            {
                value: 'webhooks',
                label: translate('settings.webhooks'),
                hidden: !webhookPermission,
            },
        ],
        [providerCompaniesProviders, providerTransferCompaniesProviders, webhookPermission],
    );

    return {
        tabs,
        selected,
        handleChange,
    };
};

export const getWebhooksSubTabsParams = (): {
    tabs: TabType[];
    selected: string;
    handleChange: (newUrl: string) => void;
} => {
    const navigate = useNavigate();
    const location = useLocation();

    const handleChange = useCallback((newUrl: string) => {
        navigate('/settings/' + newUrl);
    }, [navigate]);

    const settingsSegment = getSettingsSegment(location.pathname);
    const selected = isWebhookSettingsSegment(settingsSegment) ? settingsSegment : 'webhooks';

    const webhookPermission = useCheckPermission('WEBHOOK.LIST');
    const tabs = useMemo(
        () => [
            {
                value: 'webhooks',
                label: translate('settings.webhooks'),
                hidden: !webhookPermission,
            },
            {
                value: 'webhook_records',
                label: translate('settings.webhookRecords'),
                hidden: !webhookPermission,
            },
            {
                value: 'webhook_history',
                label: translate('settings.webhookHistory'),
                hidden: !webhookPermission,
            },
        ],
        [webhookPermission],
    );

    return {
        tabs,
        selected,
        handleChange,
    };
};
